import { getIdToken } from '../auth/cognitoAuth';
import { awsConfig } from '../aws-config';

async function request(path, options = {}) {
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not signed in.');
  }

  const response = await fetch(`${awsConfig.apiBaseUrl}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const message = body?.message || `Request failed with status ${response.status}`;
    // Lumen's 502s carry the underlying exception in `error`; show it so a
    // failure is diagnosable from the page instead of only from CloudWatch.
    const detail = typeof body?.error === 'string' && body.error && body.error !== message ? ` (${body.error})` : '';
    throw new Error(`${message}${detail}`);
  }

  return body;
}

export function getStats() {
  return request('/stats');
}

// `from`/`to` here are full 'YYYY-MM-DDTHH:mm' datetime-local strings - this
// endpoint has taken a datetime on each edge since the buckets went hourly
// (2026-07-21). Do NOT add from_time/to_time here; that split belongs to
// getTransactionHistory() below, whose from/to are plain dates.
export function getStatsHistory({ from, to } = {}) {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const qs = new URLSearchParams(params).toString();
  return request(`/stats-history${qs ? `?${qs}` : ''}`);
}

export function getExhaustedCallbacks({ page = 1, perPage = 25 } = {}) {
  const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  return request(`/exhausted-callbacks?${qs.toString()}`);
}

// Resend an ALREADY-SUCCESSFUL callback (2026-10-08). Returns the parsed
// body for both outcomes, like getTransaction(): a 409/422 here ("already
// being queued", "not a successful callback") is an answer to show the
// operator, not a broken request.
export async function resendCallback(merchantOrderId, requestedBy) {
  try {
    const body = await request('/resend-callback', {
      method: 'POST',
      body: JSON.stringify({ merchant_order_id: merchantOrderId, requested_by: requestedBy || null }),
    });
    return { ok: true, ...body };
  } catch (err) {
    return { ok: false, message: err.message || 'Resend failed.' };
  }
}

export function retryCallback(merchantOrderId) {
  return request('/retry-callback', {
    method: 'POST',
    body: JSON.stringify({ merchant_order_id: merchantOrderId }),
  });
}

// Transaction audit lookup (2026-07-28) - 404 ("nothing found for this ID")
// and 202 ("CloudWatch fallback search didn't finish in time, try again")
// are both expected, valid answers here, not request failures, so this
// bypasses request()'s throw-on-!ok behavior and returns the parsed body
// either way. Every other field this module exposes only ever represents a
// genuine success shape, which is why this one function doesn't reuse
// request() as-is.
export async function getTransaction(merchantOrderId) {
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not signed in.');
  }

  const qs = new URLSearchParams({ merchant_order_id: merchantOrderId });
  const response = await fetch(`${awsConfig.apiBaseUrl}/transaction?${qs.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const body = await response.json().catch(() => null);

  if (!response.ok && response.status !== 404 && response.status !== 202) {
    const message = body?.message || `Request failed with status ${response.status}`;
    // Lumen's 502s carry the underlying exception in `error`; show it so a
    // failure is diagnosable from the page instead of only from CloudWatch.
    const detail = typeof body?.error === 'string' && body.error && body.error !== message ? ` (${body.error})` : '';
    throw new Error(`${message}${detail}`);
  }

  return { ...body, _pending: response.status === 202 };
}

// Last N transactions this pipeline has touched, most recent first
// (2026-07-28) - a plain success/failure request, unlike getTransaction()
// above, since there's no CloudWatch-fallback/pending-search shape to
// account for here.
export function getRecentTransactions(limit = 10) {
  const qs = new URLSearchParams({ limit: String(limit) });
  return request(`/recent-transactions?${qs.toString()}`);
}

// Today's (Manila day) deposit (QP2M)/withdrawal (ICRT) amount totals
// (2026-08-14), for the real-time transaction monitoring page - backed by
// CallbackStatsStore::recordAmount() on the Lumen side (see
// P2MDashboard::transactionTotals()'s docblock). Success-only, same scope
// as getRecentTransactions() below - a plain success/failure request, no
// params.
export function getTransactionTotals() {
  return request('/transaction-totals');
}

// Live transactions-per-second (2026-08-19), for the Live summary tab's
// "live TPS" card - rolling 10s-average across QP2M/ICRT, any outcome
// (success, failed, or exhausted attempt - not success-only like
// getTransactionTotals() above), backed by CallbackStatsStore::recordTps()
// on the Lumen side (see P2MDashboard::transactionTps()'s docblock). Plain
// success/failure request, no params.
export function getTransactionTps() {
  return request('/transaction-tps');
}

// Historical, filterable transaction search (2026-08-14), for the
// Transaction Monitoring page's "History" tab - backed by DynamoDB
// (TransactionHistoryStore on the Lumen side), so unlike
// getTransactionTotals() above this covers every transaction_type over an
// arbitrary date range, not just today's QP2M/ICRT, and can find a
// transaction long after its Redis record has expired. `from`/`to` are
// plain YYYY-MM-DD dates (defaults to the last 7 days server-side when
// omitted); `transactionType`/`status`/`merchantId` are optional exact-
// match filters; `cursor` is opaque, echoed straight back from a previous
// response's `next_cursor` to page further back in time (same
// round-trip convention as getAccessLogs()'s nextToken).
export function getTransactionHistory({
  from,
  to,
  // 'HH:MM', optional, narrows within the date range. Separate from
  // from/to because those stay plain dates for every existing caller.
  fromTime,
  toTime,
  transactionType,
  status,
  merchantId,
  // TRANSACTION status, a separate axis from `status` (which is CALLBACK
  // DELIVERY status) - 2026-08-26. Tri-state: '1' rejected, '0' not
  // rejected, undefined either. `rejectionCode` narrows to one code.
  rejected,
  rejectionCode,
  limit = 25,
  cursor,
} = {}) {
  const params = { limit: String(limit) };
  if (from) params.from = from;
  if (to) params.to = to;
  if (fromTime) params.from_time = fromTime;
  if (toTime) params.to_time = toTime;
  if (transactionType) params.transaction_type = transactionType;
  if (status) params.status = status;
  if (merchantId) params.merchant_id = merchantId;
  // Explicit undefined/null check, NOT truthiness: '0' is a meaningful
  // value here and would otherwise be silently dropped.
  if (rejected !== undefined && rejected !== null && rejected !== '') {
    params.rejected = String(rejected);
  }
  if (rejectionCode) params.rejection_code = rejectionCode;
  if (cursor) params.cursor = cursor;
  const qs = new URLSearchParams(params);
  return request(`/transaction-history?${qs.toString()}`);
}

// How many records the same search matches in total (2026-08-27), for the
// "25 records loaded out of 1,234" line. A separate call from
// getTransactionHistory() on purpose - counting in DynamoDB costs about what
// fetching costs, so it is kept off the critical path of the rows: the
// results render immediately and this resolves alongside them.
//
// Takes the same search arguments MINUS limit/cursor, which describe a page
// rather than the search. Returns { total, exact, ... }; `exact: false`
// means the server's query budget ran out and `total` is a lower bound, to
// be rendered as "1,234+".
export function getTransactionHistoryCount({
  from,
  to,
  fromTime,
  toTime,
  transactionType,
  status,
  merchantId,
  rejected,
  rejectionCode,
} = {}) {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (fromTime) params.from_time = fromTime;
  if (toTime) params.to_time = toTime;
  if (transactionType) params.transaction_type = transactionType;
  if (status) params.status = status;
  if (merchantId) params.merchant_id = merchantId;
  // Same explicit check as getTransactionHistory(): '0' is a value here.
  if (rejected !== undefined && rejected !== null && rejected !== '') {
    params.rejected = String(rejected);
  }
  if (rejectionCode) params.rejection_code = rejectionCode;
  const qs = new URLSearchParams(params).toString();
  return request(`/transaction-history-count${qs ? `?${qs}` : ''}`);
}

// Search by merchant_id/instruction_id/qrph_invoice instead of
// merchant_order_id (2026-08-03) - a scan over what's currently in Redis
// (see P2MDashboard::searchTransactions()'s docblock for the known
// limitations), returning a LIST of matches rather than one record, since
// e.g. merchant_id can match many transactions. A plain success/failure
// request like getRecentTransactions() - no 404/202 special-casing like
// getTransaction() needs.
export function searchTransactions(field, value) {
  const qs = new URLSearchParams({ field, value });
  return request(`/transaction-search?${qs.toString()}`);
}

// Merchant directory (2026-08-03) - sourced from a Redis key this repo
// doesn't otherwise write (see P2MDashboard::merchants()'s docblock), no
// pagination/filtering on the backend - the whole list comes back in one
// response and this page filters client-side.
export function getMerchants() {
  return request('/merchants');
}

// "Transaction Pending" page (2026-08-03) - two independent cards, two
// independent requests, so a failure/slowness in one (e.g. the IBFT MySQL
// connection) never blocks the other from loading. Paginated (2026-08-04),
// same page/per_page convention getExhaustedCallbacks() already uses.
export function getPendingJava({ page = 1, perPage = 25, q = '' } = {}) {
  const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (q && q.trim()) qs.set('q', q.trim());
  return request(`/pending-java?${qs.toString()}`);
}

// "Awaiting Bancnet response" card (2026-10-10): p2m:txn records with no
// p2m:callback record - Leg 3 seen, final Bancnet response never arrived.
export function getPendingAwaiting({ page = 1, perPage = 25, q = '' } = {}) {
  const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (q && q.trim()) qs.set('q', q.trim());
  return request(`/pending-awaiting?${qs.toString()}`);
}

// "Send status to Java" for a stuck DEPOSIT (2026-10-10). Separate from
// updateJavaPendingStatus(): instapay-router's update-status endpoint is the
// withdrawal handler, so for deposits Lumen queues the QP2M callback itself.
export function updateAwaitingDepositStatus({ merchantOrderId, instructionId, status, errorMessage, updatedBy }) {
  return request('/pending-awaiting/update-status', {
    method: 'POST',
    body: JSON.stringify({
      merchant_order_id: merchantOrderId,
      instruction_id: instructionId,
      status,
      error_message: errorMessage,
      updated_by: updatedBy,
    }),
  });
}

// Deposits that never reached Leg 3 and whose QR expired (2026-10-12).
export function getPendingUnpaid({ page = 1, perPage = 25, q = '' } = {}) {
  const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (q && q.trim()) qs.set('q', q.trim());
  return request(`/pending-unpaid?${qs.toString()}`);
}

// Reject one of those to Java. Accepts the modals' submitJava argument shape;
// status is always "rejected" and errorMessage is the rejection code.
export function rejectUnpaidDeposit({ merchantOrderId, errorMessage, updatedBy }) {
  return request('/pending-unpaid/reject', {
    method: 'POST',
    body: JSON.stringify({ merchant_order_id: merchantOrderId, error_message: errorMessage, updated_by: updatedBy }),
  });
}

export function getPendingPhp({ page = 1, perPage = 25, q = '' } = {}) {
  const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (q && q.trim()) qs.set('q', q.trim());
  return request(`/pending-php?${qs.toString()}`);
}

// "Update status" action from the Transaction Pending page's detail modal
// (2026-08-04). Two distinct endpoints - the Java and PHP pending sources
// hit two different downstream systems with two different payload shapes
// (see P2MDashboard::updateJavaPendingStatus()/updatePhpPendingStatus()'s
// docblocks) - so this stays two functions rather than one with a `source`
// switch, matching how getPendingJava()/getPendingPhp() are already split.
//
// clientReferenceNo (not "transactionId") - corrected 2026-08-05, per the
// user's clarification that PHP/ibft's own identifier columns are
// reference_no and client_reference_no (there's no transaction_id column
// on that table at all - Lumen maps client_reference_no onto
// instapay-direct's own `transaction_id` request field server-side, see
// P2MDashboard::updatePhpPendingStatus()'s docblock).
//
// isMerchant added 2026-08-06 - instapay-direct's real API requires it (a
// real 422 otherwise); passed straight through from the pending-php row's
// own is_merchant field, no transformation here.
export function updatePhpPendingStatus({ referenceNo, clientReferenceNo, status, errorMessage, updatedBy, isMerchant }) {
  return request('/pending-php/update-status', {
    method: 'POST',
    body: JSON.stringify({
      reference_no: referenceNo,
      client_reference_no: clientReferenceNo,
      status,
      error_message: errorMessage,
      updated_by: updatedBy,
      is_merchant: isMerchant,
    }),
  });
}

// instructionId added 2026-08-06 - the Java-side counterpart to PHP
// Pending's reference_no (see the field-naming note above). Optional,
// forwarded as-is - not required the way is_merchant is for PHP Pending.
export function updateJavaPendingStatus({ merchantOrderId, instructionId, status, errorMessage, updatedBy }) {
  return request('/pending-java/update-status', {
    method: 'POST',
    body: JSON.stringify({
      merchant_order_id: merchantOrderId,
      instruction_id: instructionId,
      status,
      error_message: errorMessage,
      updated_by: updatedBy,
    }),
  });
}

// Rejected (non-empty rejection_code) transactions for today, for the
// table under Transaction Monitoring's live feed (2026-08-24). Returns the
// newest `limit` rows PLUS the day's true total_count/total_amount, which
// come from separate Redis counters rather than from the returned rows -
// so the header stays accurate even when the row list is capped during a
// rejection burst. See P2MDashboard::transactionRejected()'s docblock.
export function getRejectedTransactions({ limit = 50 } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  return request(`/transaction-rejected?${qs.toString()}`);
}

// "Access logs" page (2026-08-06) - raw log lines from one of several
// CloudWatch Logs groups the BFF reads directly (not proxied to Lumen - see
// accessLogs.js on the BFF side). `source` is a key of the BFF's
// ACCESS_LOG_SOURCES map (index.js) - 'router'/'direct' for the two Access
// Logs cards, or one of the 5 "InstapayApp Logs" dropdown slugs (see
// data/instapayAppLogSources.js). `nextToken` (optional) is CloudWatch's
// own opaque pagination cursor, echoed straight back from a previous
// response's `next_token` - omit it to fetch the first page. `allDates`
// (optional, 2026-08-13) - false/omitted keeps the original "today only"
// (Manila time) scope; true searches every date CloudWatch still has for
// that log group instead of just today.
export function getAccessLogs(source, nextToken, allDates) {
  const qs = new URLSearchParams({ source });
  if (nextToken) {
    qs.set('next_token', nextToken);
  }
  if (allDates) {
    qs.set('all_dates', 'true');
  }
  return request(`/access-logs?${qs.toString()}`);
}

// Real full-text search over one log group (2026-08-13) - unlike the
// LogGroupCard/AccessLogCard "Filter loaded logs" box (a client-side
// substring filter over whatever getAccessLogs() pages have already been
// fetched into the browser), this runs server-side via CloudWatch Logs
// Insights (accessLogsSearch.js on the BFF side) and scans the full
// requested time range in one call - added after a real gap where the
// loaded-events filter missed a match that simply hadn't been paged in yet.
// `source`/`allDates` mean the same thing as getAccessLogs() above; `q` is
// a required plain substring (not a regex - escaped BFF-side).
export function searchAccessLogs(source, q, allDates) {
  const qs = new URLSearchParams({ source, q });
  if (allDates) {
    qs.set('all_dates', 'true');
  }
  return request(`/access-logs-search?${qs.toString()}`);
}
